import React, { useState, useEffect, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { Container, Typography, Alert, Tabs, Tab, Box } from '@mui/material';
import LoadingIndicator from './LoadingIndicator';
import { getLatestReport, getProfile } from '../services/authApiService';
import { getAnalyticsFilters, getOrganizations, getPersonalAnalytics, getOrganizationalAnalytics, getOrganizationalPriorityInsights, generateOrganizationalPriorityInsights, getIndustryAnalytics, generateIndustryPriorityInsights, generateGoalReport } from '../services/analyticsService';
import FilterBar from './analytics/FilterBar';
import ReusableReportLayout from './analytics/ReusableReportLayout';
import GoalsFilterBar from './analytics/GoalsFilterBar';
import GoalAlignedReportLayout from './analytics/GoalAlignedReportLayout';
import AskQuestionsTab from './analytics/AskQuestionsTab';
import { useAuth } from '../contexts/AuthContext';
import { getCacheWithTTL, setCacheWithTTL, transformAnalyticsToReportFormat } from '../utils/reportDataTransform';

const INDUSTRY_SCOPE_ALL_USERS = '__ALL_USERS__';
const INDUSTRY_SCOPE_NON_ORG = '__NON_ORG__';

const Results = () => {
  const { user } = useAuth();
  const location = useLocation();
  const [sessionData, setSessionData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  // Profile and access
  const [profile, setProfile] = useState(null);
  const accessLevel = (profile?.access_level || 'user').toLowerCase();

  // Tabs
  const [tab, setTab] = useState('personal');
  const availableTabs = useMemo(() => {
    const t = [{ id: 'personal', label: 'Personal Results' }];
    const isManager = accessLevel === 'manager' || accessLevel === 'admin' || accessLevel === 'demo';

    if (isManager) {
      t.push({ id: 'organization', label: 'Organisation Scorecard' });
      t.push({ id: 'goals', label: 'AI Evaluation' });
    }

    // Custom Analysis available to managers, admins, and demo users
    if (accessLevel === 'manager' || accessLevel === 'admin' || accessLevel === 'demo') {
      t.push({ id: 'ask', label: 'Custom Analysis' });
    }

    if (accessLevel === 'admin') {
      t.push({ id: 'industry', label: 'Industry Benchmark' });
    }

    return t;
  }, [accessLevel]);

  // Filters and options
  const [filters, setFilters] = useState({ period: '12m', orgId: '', company: '', industry: '', startDate: '', endDate: '' });
  const [options, setOptions] = useState({ companies: [], industries: [], organizations: [], min_date: '', max_date: '' });

  // Org analytics state
  const [orgData, setOrgData] = useState(null);
  const [orgLoading, setOrgLoading] = useState(false);
  const [orgError, setOrgError] = useState('');
  const [orgGenerating, setOrgGenerating] = useState(false);

  // Industry analytics state
  const [indData, setIndData] = useState(null);
  const [indLoading, setIndLoading] = useState(false);
  const [indError, setIndError] = useState('');
  const [indGenerating, setIndGenerating] = useState(false);

  // Goals analytics state
  const [goalsData, setGoalsData] = useState(null);
  const [goalsLoading, setGoalsLoading] = useState(false);
  const [goalsError, setGoalsError] = useState('');
  const [goalsGenerating, setGoalsGenerating] = useState(false);
  const [selectedGoals, setSelectedGoals] = useState([]);

  const isSpecificOrgSelection = (orgId) =>
    Boolean(orgId) && orgId !== INDUSTRY_SCOPE_ALL_USERS && orgId !== INDUSTRY_SCOPE_NON_ORG;

  const resolveIndustryUserScope = (orgId) => {
    if (!orgId || orgId === INDUSTRY_SCOPE_ALL_USERS) return 'all';  // Handle empty string (FilterBar "All Organizations")
    if (orgId === INDUSTRY_SCOPE_NON_ORG) return 'unassigned';
    return 'assigned';
  };

  const industryOrgOptions = useMemo(() => {
    const orgs = options.organizations || [];
    return [
      { id: INDUSTRY_SCOPE_ALL_USERS, name: 'All Users' },
      { id: INDUSTRY_SCOPE_NON_ORG, name: 'Non-Org Users' },
      ...orgs
    ];
  }, [options.organizations]);

  // Personal analysis state
  const [personalData, setPersonalData] = useState(null);
  const [personalAnalysis, setPersonalAnalysis] = useState(null);
  const [personalAnalysisLoading, setPersonalAnalysisLoading] = useState(false);
  const [personalAnalysisError, setPersonalAnalysisError] = useState('');
  const [personalGenerating, setPersonalGenerating] = useState(false);

  // Global benchmark (all-users average score) - null means no benchmark shown
  const [globalAverageScore, setGlobalAverageScore] = useState(null);

  // Init: profile, session, analytics filters
  useEffect(() => {
    const init = async () => {
      if (!user) {
        setError('User not authenticated. Please log in again.');
        setLoading(false);
        return;
      }
      try {
        const p = await getProfile();
        setProfile(p);
        setFilters((f) => ({ ...f, company: p?.company || '' }));
        const data = await getLatestReport();
        setSessionData(data);
        
        // Personal analysis cache is loaded by dedicated useEffect when tab === 'personal'
        try {
          const fopts = await getAnalyticsFilters();
          const orgs = await getOrganizations();
          setOptions({
            companies: fopts?.companies || [],
            industries: fopts?.industries || [],
            organizations: orgs || [],
            min_date: fopts?.min_date || '',
            max_date: fopts?.max_date || ''
          });
          // Set org filter to user's org_id if available
          if (p?.org_id) {
            setFilters((f) => ({ ...f, orgId: p.org_id }));
          } else if (p?.company && fopts?.companies?.includes(p.company)) {
            // Fallback to company for backward compatibility
            setFilters((f) => ({ ...f, company: p.company }));
          }

          // For managers/demo, ensure orgId is set to their org_id if they have one
          if ((accessLevel === 'manager' || accessLevel === 'demo') && p?.org_id) {
            setFilters((f) => ({ ...f, orgId: p.org_id }));
          }
        } catch (e) {
          console.debug('Analytics filters load failed:', e.message);
          // Still set empty organizations array on error
          setOptions(prev => ({ ...prev, organizations: [] }));
        }
      } catch (err) {
        console.error('Init error:', err);
        setError('Failed to load user data. Please try again later.');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [user, location.key]);  // Re-fetch when navigating to this page

  // Fetch personal analysis from backend cache when personal tab is active
  useEffect(() => {
    const loadPersonalAnalysis = async () => {
      if (tab !== 'personal') return;
      if (!user?.id) return;

      try {
        const personalParams = { period: '12m', include_ai: false };
        const personalRes = await getPersonalAnalytics(personalParams);
        if (personalRes?.insights?.analysis || personalRes?.insights?.executive_summary) {
          setPersonalAnalysis(personalRes.insights);
        }
      } catch (err) {
        console.log('No cached personal analysis available');
      }
    };
    loadPersonalAnalysis();
  }, [tab, user?.id]);

  // Fetch global average score (all users) for benchmark
  useEffect(() => {
    const fetchGlobalAverage = async () => {
      try {
        const res = await getIndustryAnalytics(undefined, { period: '12m', include_ai: false });
        if (res?.summary?.average_score) {
          setGlobalAverageScore(res.summary.average_score);
        }
      } catch (err) {
        console.log('Could not fetch global average for benchmark');
      }
    };
    fetchGlobalAverage();
  }, []);

  // Fetch org analytics (for organization and goals tabs)
  useEffect(() => {
    const run = async () => {
      if (tab !== 'organization' && tab !== 'goals') return;
      if (!filters.orgId) return;
      setOrgLoading(true); setOrgError('');
      try {
        const params = { ...buildParams(filters), include_ai: false };
        const res = await getOrganizationalAnalytics(filters.orgId, params);
        
        // Check for cached AI insights and merge if available
        const aiCacheKey = `analytics:org:${filters.company}|${filters.period}|${filters.startDate || ''}|${filters.endDate || ''}|ai:true`;
        const cachedAiData = getCacheWithTTL(aiCacheKey);
        if (cachedAiData && cachedAiData.insights && cachedAiData.insights.analysis) {
          res.insights = cachedAiData.insights;
        }

        // Check backend cache for priority insights (include_ai=false)
        try {
          const priorityParams = { ...buildParams(filters), include_ai: false };
          const priorityRes = await getOrganizationalPriorityInsights(filters.orgId, priorityParams);
          if (priorityRes && priorityRes.priority_question_insights && Object.keys(priorityRes.priority_question_insights).length > 0) {
            if (!res.insights) res.insights = {};
            res.insights.priority_question_insights = priorityRes.priority_question_insights;
            // Store priority insights timestamp for display
            if (priorityRes.analysis_generated_at) {
              res.insights.priority_insights_generated_at = priorityRes.analysis_generated_at;
            }
          }
        } catch (priorityErr) {
          // Priority insights not cached yet, user will click Generate button
          console.log('No cached priority insights available');
        }
        
        setOrgData(res);
      } catch (e) {
        setOrgError(renderError(e));
        setOrgData(null);
      } finally {
        setOrgLoading(false);
      }
    };
    run();
  }, [tab, filters.orgId, filters.period, filters.startDate, filters.endDate]);

  // Fetch industry analytics (or org analytics if orgId selected)
  useEffect(() => {
    const run = async () => {
      if (tab !== 'industry') return;
      setIndLoading(true); setIndError('');
      try {
        const orgSelected = isSpecificOrgSelection(filters.orgId);
        const userScope = resolveIndustryUserScope(filters.orgId);
        let res;

        if (orgSelected) {
          const params = { ...buildParams(filters), include_ai: false };
          res = await getOrganizationalAnalytics(filters.orgId, params);

          const aiCacheKey = `analytics:org:${filters.orgId}|${filters.period}|${filters.startDate || ''}|${filters.endDate || ''}|ai:true`;
          const cachedAiData = getCacheWithTTL(aiCacheKey);
          if (cachedAiData?.insights?.analysis) {
            res.insights = cachedAiData.insights;
          }

          // Check backend cache for priority insights (include_ai=false)
          try {
            const priorityParams = { ...buildParams(filters), include_ai: false };
            const priorityRes = await getOrganizationalPriorityInsights(filters.orgId, priorityParams);
            if (priorityRes && priorityRes.priority_question_insights && Object.keys(priorityRes.priority_question_insights).length > 0) {
              if (!res.insights) res.insights = {};
              res.insights.priority_question_insights = priorityRes.priority_question_insights;
              // Store priority insights timestamp for display
              if (priorityRes.analysis_generated_at) {
                res.insights.priority_insights_generated_at = priorityRes.analysis_generated_at;
              }
            }
          } catch (priorityErr) {
            console.log('No cached priority insights available');
          }
        } else {
          const params = { ...buildParams(filters), include_ai: false, user_scope: userScope };
          res = await getIndustryAnalytics(filters.industry || undefined, params);

          const scopeKey = `${userScope}`;
          const indKey = `${filters.industry || 'ALL'}|${scopeKey}`;
          const aiCacheKey = `analytics:ind:${indKey}|${filters.period}|${filters.startDate || ''}|${filters.endDate || ''}|ai:true`;
          const cachedAiData = getCacheWithTTL(aiCacheKey);
          if (cachedAiData?.insights?.analysis) {
            res.insights = cachedAiData.insights;
          }

          const priorityCacheKey = `analytics:ind:priority:${indKey}|${filters.period}|${filters.startDate || ''}|${filters.endDate || ''}|ai:true`;
          const cachedPriorityData = getCacheWithTTL(priorityCacheKey);
          if (cachedPriorityData?.priority_question_insights) {
            if (!res.insights) res.insights = {};
            res.insights.priority_question_insights = cachedPriorityData.priority_question_insights;
            // Also load cached timestamp
            if (cachedPriorityData.analysis_generated_at) {
              res.insights.priority_insights_generated_at = cachedPriorityData.analysis_generated_at;
            }
          }
        }

        setIndData(res);
      } catch (e) {
        setIndError(renderError(e));
        setIndData(null);
      } finally {
        setIndLoading(false);
      }
    };
    run();
  }, [tab, filters.orgId, filters.industry, filters.period, filters.startDate, filters.endDate]);

  const handleTabChange = (_e, value) => setTab(value);
  const handleFilterChange = (f) => setFilters(f);

  const buildParams = (f) => ({
    period: f.period,
    ...(f.period === 'custom' ? { start_date: f.startDate, end_date: f.endDate } : {}),
  });

  const renderError = (e) => {
    if (!e) return '';
    if (e.code === 'COSMOS_DISABLED') return 'Analytics unavailable (Cosmos DB not enabled).';
    if (e.code === 'FORBIDDEN') return 'You do not have access to this data.';
    if (e.code === 'INVALID_DATES') return 'Please provide both start and end dates for custom period.';
    return e.message || 'Failed to load analytics.';
  };

  const generateOrgAnalysis = async () => {
    if (!filters.orgId) return;
    setOrgGenerating(true); setOrgError('');
    try {
      const cacheKey = `analytics:org:${filters.orgId}|${filters.period}|${filters.startDate || ''}|${filters.endDate || ''}|ai:true`;
      // Always make fresh API call for AI analysis (no cache check)
      const params = { ...buildParams(filters), include_ai: true };
      const res = await getOrganizationalAnalytics(filters.orgId, params);
      // Preserve existing priority insights when updating with new executive summary
      setOrgData(prev => ({
        ...res, // New data from the API
        insights: {
          ...res.insights, // New executive summary, etc.
          priority_question_insights: prev?.insights?.priority_question_insights || {} // Keep old priority insights
        }
      }));
      setCacheWithTTL(cacheKey, res);
    } catch (e) {
      setOrgError(renderError(e));
    } finally {
      setOrgGenerating(false);
    }
  };

  const generateOrgPriorityInsights = async () => {
    if (!filters.orgId) return;
    setOrgGenerating(true); setOrgError('');
    try {
      const params = buildParams(filters);
      const res = await generateOrganizationalPriorityInsights(filters.orgId, params);

      // Cache the priority insights
      const priorityCacheKey = `analytics:org:priority:${filters.orgId}|${filters.period}|${filters.startDate || ''}|${filters.endDate || ''}|ai:true`;
      setCacheWithTTL(priorityCacheKey, res);

      setOrgData(prev => ({
        ...prev,
        insights: {
          ...prev.insights,
          priority_question_insights: res.priority_question_insights,
          priority_insights_generated_at: res.analysis_generated_at
        }
      }));
    } catch (e) {
      setOrgError(renderError(e));
    } finally {
      setOrgGenerating(false);
    }
  };

  const generateIndAnalysis = async () => {
    setIndGenerating(true); setIndError('');
    try {
      const orgSelected = isSpecificOrgSelection(filters.orgId);
      const userScope = resolveIndustryUserScope(filters.orgId);
      let res;
      let cacheKey;

      if (orgSelected) {
        const params = { ...buildParams(filters), include_ai: true };
        cacheKey = `analytics:org:${filters.orgId}|${filters.period}|${filters.startDate || ''}|${filters.endDate || ''}|ai:true`;
        res = await getOrganizationalAnalytics(filters.orgId, params);
      } else {
        const params = { ...buildParams(filters), include_ai: true, user_scope: userScope };
        const indKey = `${filters.industry || 'ALL'}|${userScope}`;
        cacheKey = `analytics:ind:${indKey}|${filters.period}|${filters.startDate || ''}|${filters.endDate || ''}|ai:true`;
        res = await getIndustryAnalytics(filters.industry || undefined, params);
      }

      // Update indData with new AI insights, preserving existing priority insights
      setIndData(prev => ({
        ...res, // New data from the API
        insights: {
          ...res.insights, // New executive summary, etc.
          priority_question_insights: prev?.insights?.priority_question_insights || {} // Keep old priority insights
        }
      }));
      setCacheWithTTL(cacheKey, res);
    } catch (e) {
      setIndError(renderError(e));
    } finally {
      setIndGenerating(false);
    }
  };

  const generateIndPriorityInsights = async () => {
    setIndGenerating(true); setIndError('');
    try {
      const orgSelected = isSpecificOrgSelection(filters.orgId);
      const userScope = resolveIndustryUserScope(filters.orgId);
      let res;
      let priorityCacheKey;

      if (orgSelected) {
        const params = buildParams(filters);
        priorityCacheKey = `analytics:org:priority:${filters.orgId}|${filters.period}|${filters.startDate || ''}|${filters.endDate || ''}|ai:true`;
        res = await generateOrganizationalPriorityInsights(filters.orgId, params);
      } else {
        const params = { ...buildParams(filters), user_scope: userScope };
        const indKey = `${filters.industry || 'ALL'}|${userScope}`;
        priorityCacheKey = `analytics:ind:priority:${indKey}|${filters.period}|${filters.startDate || ''}|${filters.endDate || ''}|ai:true`;
        res = await generateIndustryPriorityInsights(filters.industry || undefined, params);
      }

      setCacheWithTTL(priorityCacheKey, res);

      // Update indData with new priority insights, preserving existing AI insights
      setIndData(prev => ({
        ...prev,
        insights: {
          ...prev.insights, // Keep existing AI insights
          priority_question_insights: res.priority_question_insights,
          priority_insights_generated_at: res.analysis_generated_at
        }
      }));
    } catch (e) {
      setIndError(renderError(e));
    } finally {
      setIndGenerating(false);
    }
  };

  // Generate personal analysis
  const generatePersonalAnalysis = async () => {
    setPersonalGenerating(true);
    setPersonalAnalysisError('');
    try {
      const cacheKey = `personal:analysis:${user.id}|ai:true`;
      // Always make fresh API call for AI analysis (no cache check)
      const params = { period: '12m', include_ai: true };
      const data = await getPersonalAnalytics(params);
      setPersonalAnalysis(data.insights);
      setCacheWithTTL(cacheKey, data.insights);
    } catch (error) {
      setPersonalAnalysisError(error.message || 'Failed to generate analysis');
    } finally {
      setPersonalGenerating(false);
    }
  };

  // Personal analysis is now button-triggered only, no auto-fetch

  // Goals report cache checking - when filters or selected goals change
  useEffect(() => {
    if (tab !== 'goals' || !filters.orgId || selectedGoals.length === 0) {
      return;
    }

    const cacheKey = `analytics:categories:${filters.orgId}|all|${filters.period}|${filters.startDate || ''}|${filters.endDate || ''}`;
    const cached = getCacheWithTTL(cacheKey);

    if (cached) {
      setGoalsData(cached);
      setGoalsError('');
    } else {
      setGoalsData(null);
    }
  }, [tab, filters.orgId, filters.period, filters.startDate, filters.endDate, selectedGoals]);

  // Generate goal-aligned report
  const generateGoalReportHandler = async (forceRegenerateOrEvent = false) => {
    const forceRegenerate = typeof forceRegenerateOrEvent === 'boolean'
      ? forceRegenerateOrEvent
      : false;

    if (forceRegenerateOrEvent && typeof forceRegenerateOrEvent === 'object' && typeof forceRegenerateOrEvent.preventDefault === 'function') {
      forceRegenerateOrEvent.preventDefault();
    }
    if (!filters.orgId) {
      setGoalsError('Please select organization');
      return;
    }

    console.log('[GoalReport] Starting generation:', {
      orgId: filters.orgId,
      forceRegenerate
    });

    setGoalsGenerating(true);
    setGoalsError('');

    try {
      // Generate goal-aligned report with all responses
      const params = {
        period: filters.period,
        start_date: filters.startDate || null,
        end_date: filters.endDate || null,
        force_regenerate: forceRegenerate
      };

      console.log('[GoalReport] Calling generateGoalReport with params:', params);
      const report = await generateGoalReport(filters.orgId, params);
      console.log('[GoalReport] Report received successfully');
      setGoalsData(report);

      // Cache the complete report (categories = empty/all)
      const cacheKey = `analytics:categories:${filters.orgId}|all|${filters.period}|${filters.startDate || ''}|${filters.endDate || ''}`;
      setCacheWithTTL(cacheKey, report);
    } catch (error) {
      console.error('[GoalReport] Error generating report:', {
        message: error.message,
        code: error.code,
        status: error.status
      });
      setGoalsError(renderError(error));
    } finally {
      setGoalsGenerating(false);
    }
  };

  // Handler for regenerating report (bypasses cache)
  const regenerateGoalReportHandler = async () => {
    await generateGoalReportHandler(true);
  };

  const actualSessionData = sessionData;

  const resolvedOrgName =
    orgData?.filters_applied?.company ||
    (filters?.orgId
      ? options.organizations?.find((org) => org.id === filters.orgId)?.name
      : null) ||
    profile?.company ||
    'Organization';

  const industryReportName = useMemo(() => {
    if (isSpecificOrgSelection(filters.orgId)) {
      const org = options.organizations?.find((o) => o.id === filters.orgId);
      return org?.name || 'Organization';
    }
    if (filters.orgId === INDUSTRY_SCOPE_ALL_USERS) {
      return filters.industry ? `${filters.industry} - All Users` : 'All Users';
    }
    if (filters.orgId === INDUSTRY_SCOPE_NON_ORG) {
      return filters.industry ? `${filters.industry} - Non-Org Users` : 'Non-Org Users';
    }
    if (!filters.orgId) {
      return filters.industry ? `${filters.industry} - All Organisations` : 'All Organisations';
    }
    return filters.industry || 'Industry';
  }, [filters.orgId, filters.industry, options.organizations]);

  if (loading) return <LoadingIndicator />;
  if (error) return <Alert severity="error">{error}</Alert>;
  if (!sessionData) return <Typography>No report data found.</Typography>;

  return (
    <Container>
      <Tabs value={tab} onChange={handleTabChange} sx={{ mb: 2 }}>
        {availableTabs.map((t) => (
          <Tab key={t.id} label={t.label} value={t.id} />
        ))}
      </Tabs>

      {tab === 'personal' && (
        <Box>
          {!actualSessionData?.meta?.report_data ? (
            <>
              <Typography variant="h5" gutterBottom>No report data available in the latest session.</Typography>
              <Typography variant="body2" color="text.secondary">
                {actualSessionData 
                  ? `Found session with ${actualSessionData.responses?.length || 0} responses, but no report data generated.`
                  : 'No sessions found for this user.'}
              </Typography>
            </>
          ) : (
            <ReusableReportLayout
              data={actualSessionData}
              loading={personalAnalysisLoading}
              error={personalAnalysisError}
              onGenerateAnalysis={generatePersonalAnalysis}
              generating={personalGenerating}
              scope="personal"
              analysis={personalAnalysis}
              userName=""
              benchmarkScore={globalAverageScore}
            />
          )}
        </Box>
      )}

      {tab === 'organization' && (
        <Box>
          <FilterBar
            activeTab="organization"
            filters={filters}
            onChange={handleFilterChange}
            companies={options.companies}
            industries={options.industries}
            organizations={options.organizations}
            showOrganizationSelector={accessLevel === 'admin'}
          />
          <ReusableReportLayout
            data={orgData}
            loading={orgLoading}
            error={orgError}
            onGenerateAnalysis={generateOrgAnalysis}
            onGeneratePriorityInsights={generateOrgPriorityInsights}
            generating={orgGenerating}
            scope="organization"
            companyName={resolvedOrgName}
            benchmarkScore={globalAverageScore}
          />
        </Box>
      )}

      {tab === 'ask' && (
        <Box>
          {(accessLevel === 'manager' || accessLevel === 'admin' || accessLevel === 'demo') && (
            <FilterBar
              activeTab="ask"
              filters={filters}
              onChange={handleFilterChange}
              organizations={options.organizations}
              showOrganizationSelector={true}
            />
          )}
          <AskQuestionsTab orgId={filters.orgId} />
        </Box>
      )}

      {tab === 'industry' && (
        <Box>
          <FilterBar
            activeTab="industry"
            filters={filters}
            onChange={handleFilterChange}
            companies={options.companies}
            industries={options.industries}
            organizations={industryOrgOptions}
            showOrganizationSelector={true}
          />
          <ReusableReportLayout
            data={indData}
            loading={indLoading}
            error={indError}
            onGenerateAnalysis={generateIndAnalysis}
            onGeneratePriorityInsights={generateIndPriorityInsights}
            generating={indGenerating}
            scope="industry"
            companyName={industryReportName}
            benchmarkScore={globalAverageScore}
          />
        </Box>
      )}

      {tab === 'goals' && (
        <Box>
          <GoalsFilterBar
            filters={filters}
            onFilterChange={handleFilterChange}
            selectedGoals={selectedGoals}
            onGoalsChange={setSelectedGoals}
            organizations={options.organizations}
            accessLevel={accessLevel}
          />

          {selectedGoals.length === 0 ? (
            <Box sx={{ textAlign: 'center', py: 8 }}>
              <Typography variant="h6" color="text.secondary" gutterBottom>
                Preparing the AI Evaluation configuration...
              </Typography>
            </Box>
          ) : filters.orgId ? (
              <GoalAlignedReportLayout
                data={goalsData}
                loading={goalsLoading}
                error={goalsError}
                onRegenerate={regenerateGoalReportHandler}
                generating={goalsGenerating}
              />
          ) : null}
        </Box>
      )}
    </Container>
  );
};

export default Results;
